import { use } from 'react';
import { SelectedCompanyContext } from '@/contexts/selected-company-context';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';

/**
 * Hook to access the currently selected company (for super admin) or effective company.
 * When used inside SelectedCompanyProvider, returns shared state. When used outside
 * (e.g. in AuthProvider), returns a fallback so the hook never throws.
 */
export function useSelectedCompany() {
  const context = use(SelectedCompanyContext);
  const { currentUser } = useSuperAdmin();

  if (context !== undefined) {
    return context;
  }

  // Outside provider (e.g. AuthProvider, useTranslationOverrides): return safe fallback
  return {
    selectedCompany: null,
    effectiveCompany: currentUser?.company ?? null,
    selectCompany: () => {},
    clearSelectedCompany: () => {},
    canSelectCompany: false,
    isInitialized: true,
    companies: [],
  };
}

/**
 * Stable segment for TanStack Query keys on company-scoped API data, so detail and nested caches
 * are not reused when the user switches company (e.g. multi-membership or super admin).
 */
export function useCompanyQueryScope(): number | 'unscoped' {
  const { effectiveCompany } = useSelectedCompany();
  return effectiveCompany?.id ?? 'unscoped';
}
